import React, { useMemo } from "react";
import { OrgHomeRegularDrawer, OrgHomeRouteKey } from "./OrgHomeNavigation";
import { OrgStoresScreen } from "./OrgStores/OrgStores";
import { DrawerContentComponentProps } from "@react-navigation/drawer";
import { OrgHomeMasterComponent } from "./OrgHomeMasterComponent";

import { OrgSpaces } from "./OrgSpaces/OrgSpaces";

import { ORG_HOME_DRAWER_WIDTH } from "./ORG_HOME_DRAWER_WIDTH";

import { WorkspaceFilesScreen } from "./WorkspaceFiles/WorkspaceFiles";
import { WorkspaceTodosScreen } from "./WorkspaceTodos/WorkspaceTodos";
import { WorkspaceSignalsScreen } from "./WorkspaceSignals/WorkspaceSignals";
import { graphql, useLazyLoadQuery } from "react-relay";
import { OrgHomeRegularQuery } from "./__generated__/OrgHomeRegularQuery.graphql";
import { OrgHomeMasterComponent_query$key } from "./__generated__/OrgHomeMasterComponent_query.graphql";
import { WorkspaceIdProvider } from "./workspaceId";
import { usePersistedWorkspaceId } from "../../OS/Login/State";

import { useForceWorkspaceTimeZone } from "../../OS/WorkspaceTimezone";
import { WorkspaceProductsScreen } from "./WorkspaceProducts/WorkspaceProducts";
import { WorkspaceRoutines } from "./WorkspaceRoutines/WorkspaceRoutines";
import { WorkspaceAnalyticsScreen } from "./WorkspaceAnalytics/WorkspaceAnalytics";

const EXCLUDED_FROM_AUTOMATIC_NAVIGATION_REFRESH = ["WorkspaceAnalyticsMain"];
export function OrgHomeRegular(props: { fetchKey: string }) {
  const data = useLazyLoadQuery<OrgHomeRegularQuery>(
    graphql`
      query OrgHomeRegularQuery {
        ...OrgHomeMasterComponent_query
        viewer {
          workspaces {
            id
            timezone
            permission {
              read
              write
            }
          }
        }
      }
    `,
    {},
    {
      fetchPolicy: "store-and-network",
      fetchKey: props.fetchKey,
    }
  );

  const { currentWorkspaceId, setCurrentWorkspaceId } =
    usePersistedWorkspaceId();

  const persistedWorkspace = currentWorkspaceId
    ? data.viewer?.workspaces?.find((ws) => ws.id === currentWorkspaceId)
    : null;

  const workspace = persistedWorkspace ?? data.viewer?.workspaces?.[0];

  const tz = workspace?.timezone;

  useForceWorkspaceTimeZone(tz ?? null);

  if (!workspace) {
    return null;
  }

  return (
    <WorkspaceIdProvider
      workspaceId={workspace.id}
      workspacePermissions={workspace.permission}
    >
      <OrgHomeRegularDrawer.Navigator
        screenOptions={{
          headerShown: false,
          drawerType: "permanent",
          drawerStyle: {
            width: ORG_HOME_DRAWER_WIDTH,
          },
        }}
        drawerContent={(props) => (
          <DrawerContent
            {...props}
            query={data}
            workspaceId={workspace.id}
            onSelectWorkspace={(id) => {
              const currentRouteName =
                props.state.routes[props.state.index]?.state?.routeNames?.[0];

              if (
                currentRouteName &&
                !EXCLUDED_FROM_AUTOMATIC_NAVIGATION_REFRESH.includes(
                  currentRouteName
                )
              ) {
                props.navigation.navigateDeprecated(currentRouteName);
              }
              setCurrentWorkspaceId(id);
            }}
          />
        )}
      >
        <OrgHomeRegularDrawer.Screen
          name="OrgStores"
          component={OrgStoresScreen}
        />

        <OrgHomeRegularDrawer.Screen name="OrgSpaces" component={OrgSpaces} />

        <OrgHomeRegularDrawer.Screen
          name="WorkspaceFiles"
          component={WorkspaceFilesScreen}
        />
        <OrgHomeRegularDrawer.Screen
          name="WorkspaceRoutines"
          component={WorkspaceRoutines}
        />
        <OrgHomeRegularDrawer.Screen
          name="WorkspaceTodos"
          component={WorkspaceTodosScreen}
        />
        <OrgHomeRegularDrawer.Screen
          name="WorkspaceSignals"
          component={WorkspaceSignalsScreen}
        />
        <OrgHomeRegularDrawer.Screen
          name="WorkspaceProducts"
          component={WorkspaceProductsScreen}
        />
        <OrgHomeRegularDrawer.Screen
          name="WorkspaceAnalytics"
          component={WorkspaceAnalyticsScreen}
        />
      </OrgHomeRegularDrawer.Navigator>
    </WorkspaceIdProvider>
  );
}

function DrawerContent(
  props: DrawerContentComponentProps & {
    query: OrgHomeMasterComponent_query$key;
    onSelectWorkspace: (workspaceId: string) => void;
    workspaceId: string;
  }
) {
  const selectedRouteName = useMemo(() => {
    return props.state.routeNames[props.state.index] as OrgHomeRouteKey;
  }, [props.state.index, props.state.routeNames]);
  return (
    <OrgHomeMasterComponent
      navigation={props.navigation as any}
      selectedRoute={selectedRouteName}
      query={props.query}
      onSelectWorkspace={props.onSelectWorkspace}
      workspaceId={props.workspaceId}
    />
  );
}
